/**
 * Reversal History — Admin Audit View
 *
 * Read-only dashboard that aggregates every payment reversal performed
 * via the admin_reverse_payment RPC. Surfaces admin attribution, the full
 * per-referrer clawback breakdown (USDT + shares), the email + in-app
 * notification dispatch outcome, and the financial_audit_log cross-check.
 *
 * Data source: /api/admin/reversal-history (service-role read, no PII
 * beyond what already appears in PaymentManager).
 */

import React, { useState, useEffect } from 'react'
import { getSessionHeaders } from '../../lib/serverSession'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

interface Clawback {
  referrer_id: number
  referrer: { id: number; username?: string; email?: string; full_name?: string } | null
  usdt_clawed: number
  shares_clawed: number
}

interface DispatchStatus {
  email?: { attempted?: boolean; success?: boolean; reason?: string | null; status?: number | null }
  notification?: { attempted?: boolean; success?: boolean; reason?: string | null }
}

interface ReversalRow {
  audit_id: string
  reversed_at: string
  payment_id: string
  description?: string
  admin: { id: number; username?: string; email?: string; full_name?: string } | null
  buyer: { id: number; username?: string; email?: string; full_name?: string } | null
  payment: {
    id: string
    amount: number
    currency: string | null
    network: string | null
    transaction_hash: string | null
    status: string
    reversed_at: string | null
    reversal_reason: string | null
  } | null
  reversal_reason: string | null
  clawbacks: Clawback[]
  csp_reversed: number | null
  mlc_reversed: number | null
  commission_tx_cancelled: number | null
  balances_adjusted: number | null
  share_purchase_id: string | null
  dispatch_status: DispatchStatus | null
  financial_audit_present: boolean
}

const dt = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—')
const fmtAmt = (n: number | null | undefined, d = 2) =>
  typeof n === 'number' ? n.toFixed(d) : Number(n ?? 0).toFixed(d)
const fmtShares = (n: number | null | undefined) => {
  const v = Number(n ?? 0)
  return v >= 0.01 ? v.toFixed(4) : v.toFixed(8)
}

const Pill: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
  <span style={{
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 600,
    background: ok ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
    color: ok ? '#86EFAC' : '#FCA5A5',
    border: `1px solid ${ok ? '#16A34A' : '#B91C1C'}`
  }}>
    {ok ? '✓' : '✗'} {label}
  </span>
)

const ReversalCard: React.FC<{ row: ReversalRow; expanded: boolean; onToggle: () => void }> = ({ row, expanded, onToggle }) => {
  const totalUsdt = row.clawbacks.reduce((s, c) => s + Number(c.usdt_clawed || 0), 0)
  const totalShares = row.clawbacks.reduce((s, c) => s + Number(c.shares_clawed || 0), 0)
  const emailOk = !!row.dispatch_status?.email?.success
  const emailAttempted = !!row.dispatch_status?.email?.attempted
  const notifOk = !!row.dispatch_status?.notification?.success

  return (
    <div style={{ background: '#1F2937', border: '1px solid #374151', borderRadius: '10px', overflow: 'hidden' }}>
      <div
        onClick={onToggle}
        style={{ padding: '14px 16px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '1.5fr 1.2fr 1fr 1.2fr auto', gap: '12px', alignItems: 'center' }}
      >
        <div>
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Reversed</div>
          <div style={{ fontWeight: 600 }}>{dt(row.reversed_at)}</div>
        </div>
        <div>
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Buyer</div>
          <div style={{ fontWeight: 600 }}>{row.buyer?.username || row.buyer?.email || `user ${row.payment?.id?.slice(0, 8) || '—'}`}</div>
        </div>
        <div>
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Amount</div>
          <div style={{ fontWeight: 600 }}>{fmtAmt(row.payment?.amount)} {row.payment?.currency || ''}</div>
        </div>
        <div>
          <div style={{ fontSize: '13px', color: '#9CA3AF' }}>Admin</div>
          <div style={{ fontWeight: 600 }}>{row.admin?.username || row.admin?.email || `admin ${row.admin?.id || '?'}`}</div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {emailAttempted && <Pill ok={emailOk} label="email" />}
          <Pill ok={notifOk} label="notif" />
          <Pill ok={row.financial_audit_present} label="audit" />
          <span style={{ color: '#9CA3AF', fontSize: '18px', marginLeft: '4px' }}>{expanded ? '▾' : '▸'}</span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '16px', borderTop: '1px solid #374151', background: '#111827' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Payment</div>
              <div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{row.payment_id}</div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '8px' }}>Network · Status</div>
              <div style={{ fontSize: '13px' }}>{row.payment?.network || '—'} · {row.payment?.status || '—'}</div>
              {row.payment?.transaction_hash && (
                <>
                  <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '8px' }}>Reference / Tx Hash</div>
                  <div style={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>{row.payment.transaction_hash}</div>
                </>
              )}
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Reason</div>
              <div style={{ fontSize: '13px' }}>{row.reversal_reason || '—'}</div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '8px' }}>Cascade</div>
              <div style={{ fontSize: '13px' }}>
                MLC: {row.mlc_reversed ?? 0} · CSP: {row.csp_reversed ?? 0} · Balances: {row.balances_adjusted ?? 0} · TX Cancelled: {row.commission_tx_cancelled ?? 0}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '12px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>
              Clawbacks ({row.clawbacks.length}) · Total {fmtAmt(totalUsdt, 4)} USDT · {fmtShares(totalShares)} shares
            </div>
            {row.clawbacks.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#9CA3AF' }}>No referrer clawbacks (no commissions had been paid).</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #374151' }}>
                    <th style={{ padding: '6px 8px' }}>Referrer</th>
                    <th style={{ padding: '6px 8px' }}>ID</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>USDT clawed</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Shares clawed</th>
                  </tr>
                </thead>
                <tbody>
                  {row.clawbacks.map((c) => (
                    <tr key={c.referrer_id} style={{ borderBottom: '1px solid #1F2937' }}>
                      <td style={{ padding: '6px 8px' }}>{c.referrer?.username || c.referrer?.email || '—'}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', color: '#9CA3AF' }}>{c.referrer_id}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtAmt(c.usdt_clawed, 6)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace' }}>{fmtShares(c.shares_clawed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ fontSize: '12px', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Dispatch outcomes</div>
          <div style={{ fontSize: '13px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <div>
              <strong>Email:</strong>{' '}
              {emailAttempted
                ? (emailOk
                  ? `Sent (HTTP ${row.dispatch_status?.email?.status})`
                  : `Failed — ${row.dispatch_status?.email?.reason || 'unknown'}`)
                : 'Not attempted'}
            </div>
            <div>
              <strong>In-app notification:</strong>{' '}
              {notifOk ? 'Created' : `Failed — ${row.dispatch_status?.notification?.reason || 'unknown'}`}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

const ReversalHistory: React.FC = () => {
  const [rows, setRows] = useState<ReversalRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/reversal-history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
        body: JSON.stringify({ limit: 100, offset: 0 })
      })
      const result = await response.json()
      if (!response.ok || !result.success) throw new Error(result.error || 'Failed to load reversal history')
      setRows(result.data.rows || [])
    } catch (e: any) {
      setError(e?.message || 'Network error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div style={{ padding: '20px', color: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>🔄 Reversal History</h2>
          <p style={{ margin: '4px 0 0 0', color: '#9CA3AF', fontSize: '13px' }}>
            Every payment reversal performed via the atomic admin_reverse_payment RPC,
            with full per-referrer clawback breakdown and dispatch outcomes.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ padding: '8px 16px', background: '#374151', color: '#fff', borderRadius: '8px', border: '1px solid #4B5563', cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', background: 'rgba(239,68,68,0.15)', border: '1px solid #B91C1C', borderRadius: '8px', marginBottom: '16px', color: '#FCA5A5' }}>
          {error}
        </div>
      )}

      {!loading && rows.length === 0 && !error && (
        <div style={{ padding: '24px', background: '#1F2937', border: '1px solid #374151', borderRadius: '8px', textAlign: 'center', color: '#9CA3AF' }}>
          No reversals on record.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {rows.map((r) => (
          <ReversalCard
            key={r.audit_id}
            row={r}
            expanded={expanded.has(r.audit_id)}
            onToggle={() => toggle(r.audit_id)}
          />
        ))}
      </div>
    </div>
  )
}

export default ReversalHistory
